import { FC, useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation, Link } from 'react-router-dom';
import * as yup from 'yup';
import toast from 'react-hot-toast';

import { useAuth } from '@hooks/Auth';

import InputText from '@components/elements/InputText';
import ButtonMighty from '@components/elements/ButtonMighty';
import Loader from '@components/elements/Loader';

import './LoginPage.css';

// const PHONE_REGEX = /^\+65(6|8|9)\d{7}$/

const USER_SCHEMA = yup.object().shape({
  username: yup.string().required('Username is required'),
  password: yup.string().required('Password is required'),
});

const LoginPage: FC = () => {
  const history = useHistory<any>();
  const location = useLocation<any>();

  const { user, dbUser, login } = useAuth();
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (user && dbUser) {
      if (location.state?.from) history.push(location.state?.from);
      else history.push('/home');
    }
  }, [dbUser, user, history, location]);

  const [inputForm, setInputForm] = useState<Record<string, any>>({
    username: {
      value: '',
      placeholder: 'Username',
      error: false,
    },
    password: {
      value: '',
      placeholder: 'Password',
      error: false,
      type: 'password',
    },
  });

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      setLoading(true);

      const fieldNames: string[] = Object.keys(inputForm);

      const values: Record<string, any> = {};
      fieldNames.forEach((name) => {
        if (e.target[name]) values[name] = e.target[name].value;
      });

      try {
        try {
          await USER_SCHEMA.validate(values, { abortEarly: false });
          const newForm = { ...inputForm };
          Object.keys(inputForm).forEach((key) => {
            newForm[key] = {
              ...newForm[key],
              error: false,
            };
          });
          setInputForm(newForm);
        } catch (err: any) {
          const newForm = { ...inputForm };
          err.inner.forEach((e) => {
            console.log(e);
            newForm[e.path] = {
              ...newForm[e.path],
              error: e.message,
            };
          });
          setInputForm(newForm);
          return;
        }

        try {
          await login(values.username.trim(), values.password);
          if (location.state?.from) history.push(location.state?.from);
          else history.push('/home');
        } catch (err: any) {
          if (err.code === 'UserNotConfirmedException') {
            history.push(
              `/verify${values.username ? `?username=${values.username}` : ''}`
            );
          }
          if (err.code === 'PasswordResetRequiredException') {
            history.push(
              `/reset-password?username=${values.username}&redirect=reset`
            );
          } else {
            console.error(err);
            toast.error(err.message);
          }
        }
      } catch (err: any) {
        console.error(err);
        toast.error(err.message);
      } finally {
        setLoading(false);
      }
    },
    [history, location, inputForm, login]
  );

  let content;
  if (loading) content = <Loader title="Logging in..." />;
  else {
    content = (
      <form className="login-form" onSubmit={onSubmit}>
        {['username', 'password'].map((name) => {
          const field = inputForm[name];
          return (
            <div key={`login-field-${name}`}>
              <InputText
                name={name}
                placeholder={field.placeholder}
                type={field.type || 'text'}
                value={inputForm[name].value}
                onChange={(value) => {
                  setInputForm({
                    ...inputForm,
                    [name]: {
                      ...inputForm[name],
                      value,
                      error: false,
                    },
                  });
                }}
              />
              {field.error && (
                <p className="login-field-error">{field.error}</p>
              )}
            </div>
          );
        })}

        <Link to="/reset-password">
          <div className="login-forgot-password">Forgot password</div>
        </Link>
        <div className="mt-8">
          <ButtonMighty
            type="submit"
            disabled={loading}
            loading={loading}
            color="#ff1dfc"
            text="Log in"
            borderColor="#00ff43"
            margin="0"
            textColor="#fff"
          />
        </div>

        <Link to="/signup">
          <div
            role="button"
            className="font-semibold text-center font-display text-blue"
          >
            Don&apos;t have an account?
            <br />
            Join MegaX World
          </div>
        </Link>
      </form>
    );
  }
  return (
    <div className="pt-20 login-page">
      <div className="container mx-auto md:w-108">
        <div className="px-4 py-4 mx-4 bg-white lg:px-8 lg:mx-0">
          <h1 className="mt-8 mb-8 text-6xl font-bold">Log In</h1>
          <div>{content}</div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
